// src/pages/CreateTimer.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api from '../../api/axiosInstance';
import { FiSave, FiType, FiClock, FiSliders, FiEye, FiAlertCircle } from 'react-icons/fi';

// --- Child Component: Live Timer Preview ---
const TimerPreview = ({ backgroundColor, digitColor, labelColor, endTime }) => {
    const calculateTimeLeft = useCallback(() => {
        const difference = +new Date(endTime) - +new Date();
        let timeLeft = {};

        if (difference > 0) {
            timeLeft = {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60),
            };
        }
        return timeLeft;
    }, [endTime]);

    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

    useEffect(() => {
        // Set an interval to update the countdown every second
        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        // Clear the interval on component unmount
        return () => clearInterval(timer);
    }, [endTime, calculateTimeLeft]);

    const formatTime = (value) => value.toString().padStart(2, '0');

    const timeSegments = [
        { label: 'Days', value: timeLeft.days ?? 0 },
        { label: 'Hours', value: timeLeft.hours ?? 0 },
        { label: 'Minutes', value: timeLeft.minutes ?? 0 },
        { label: 'Seconds', value: timeLeft.seconds ?? 0 },
    ];

    return (
        <div
            className="p-8 rounded-lg flex items-center justify-center transition-colors duration-300"
            style={{ backgroundColor: backgroundColor }}
        >
            <div className="flex items-center justify-center gap-4 sm:gap-6">
                {timeSegments.map((segment, index) => (
                    <div key={segment.label} className="text-center">
                        <div
                            className="text-4xl sm:text-6xl font-bold transition-colors duration-300"
                            style={{ color: digitColor }}
                        >
                            {formatTime(segment.value)}
                        </div>
                        <div
                            className="text-xs sm:text-sm uppercase tracking-wider mt-1 transition-colors duration-300"
                            style={{ color: labelColor }}
                        >
                            {segment.label}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- Main Create Timer Component ---
const CreateTimer = () => {
    const navigate = useNavigate();
    const [setBreadcrumb, setBreadcrumbLink] = useOutletContext();

    // Form State
    const [name, setName] = useState('');
    const [time, setTime] = useState('');
    const [backgroundColor, setBackgroundColor] = useState('#0066FF'); // Default to brand accent
    const [digitColor, setDigitColor] = useState('#ffffff');
    const [labelColor, setLabelColor] = useState('#ffffff');

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setBreadcrumb(['Countdown', 'Create Timer']);
        setBreadcrumbLink(['/countdown']);
        // Set a default end time 24 hours from now for the initial preview
        const defaultEndTime = new Date(Date.now() + 24 * 60 * 60 * 1000);
        const formattedDefault = defaultEndTime.toISOString().slice(0, 16);
        setTime(formattedDefault);
    }, [setBreadcrumb, setBreadcrumbLink]);

    const handleCreateTimer = async (event) => {
        event.preventDefault();
        setError('');
        if (!name || !time) {
            setError('Please fill out all required fields.');
            return;
        }

        setIsSaving(true);
        try {
            const res = await api.post(`/countdown`, {
                name,
                endTime: Math.floor(new Date(time).getTime() / 1000),
                backgroundColor,
                digitColor,
                labelColor,
            });
            if (!res.data.error) {
                navigate('/countdown'); // Navigate back to the list on success
            } else {
                setError(res.data.message || 'An unknown error occurred.');
            }
        } catch (err) {
            setError(err.message === 'Network Error' ? "No Internet Connection." : 'Failed to create timer.');
            console.error('Error creating timer:', err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="p-4 sm:p-6 bg-brand-background min-h-screen font-sans">
            {/* --- Header --- */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-brand-heading">Create New Timer</h1>
                    <p className="text-brand-paragraph mt-1">Configure and preview your timer in real-time.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* --- Left Column: Form --- */}
                <div className="lg:col-span-3 bg-white p-6 rounded-lg border border-brand-border shadow-sm">
                    <form onSubmit={handleCreateTimer}>
                        <div className="space-y-6">
                            {/* --- Section 1: Timer Details --- */}
                            <div>
                                <h3 className="text-lg font-bold text-brand-heading flex items-center gap-2 mb-3"><FiType /> Timer Details</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900">Timer Name</label>
                                        <input onChange={(e) => setName(e.target.value)} value={name} type="text" id="name" className="bg-gray-50 border border-brand-border text-gray-900 text-sm rounded-lg focus:ring-brand-accent focus:border-brand-accent block w-full p-2.5" placeholder="e.g., Product Launch" required />
                                    </div>
                                    <div>
                                        <label htmlFor="endTime" className="block mb-2 text-sm font-medium text-gray-900 flex items-center gap-2"><FiClock /> End Date & Time</label>
                                        <input onChange={(e) => setTime(e.target.value)} value={time} type="datetime-local" id="endTime" className="bg-gray-50 border border-brand-border text-gray-900 text-sm rounded-lg focus:ring-brand-accent focus:border-brand-accent block w-full p-2.5" required />
                                    </div>
                                </div>
                            </div>

                            {/* --- Section 2: Styling --- */}
                            <div>
                                <h3 className="text-lg font-bold text-brand-heading flex items-center gap-2 mb-3"><FiSliders /> Styling</h3>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="text-center">
                                        <label htmlFor="backgroundColor" className="block mb-2 text-sm font-medium text-gray-900">Background</label>
                                        <input onChange={(e) => setBackgroundColor(e.target.value)} value={backgroundColor} type="color" id="backgroundColor" className="p-1 h-12 w-full block bg-white border border-gray-200 cursor-pointer rounded-lg" required />
                                    </div>
                                    <div className="text-center">
                                        <label htmlFor="digitColor" className="block mb-2 text-sm font-medium text-gray-900">Digits</label>
                                        <input onChange={(e) => setDigitColor(e.target.value)} value={digitColor} type="color" id="digitColor" className="p-1 h-12 w-full block bg-white border border-gray-200 cursor-pointer rounded-lg" required />
                                    </div>
                                    <div className="text-center">
                                        <label htmlFor="labelColor" className="block mb-2 text-sm font-medium text-gray-900">Labels</label>
                                        <input onChange={(e) => setLabelColor(e.target.value)} value={labelColor} type="color" id="labelColor" className="p-1 h-12 w-full block bg-white border border-gray-200 cursor-pointer rounded-lg" required />
                                    </div>
                                </div>
                            </div>

                            {/* --- Error Message --- */}
                            {error && (
                                <div className="bg-red-50 text-red-700 p-3 rounded-lg flex items-center gap-2 text-sm">
                                    <FiAlertCircle /> {error}
                                </div>
                            )}

                            {/* --- Form Submission --- */}
                            <div className="pt-4 border-t border-gray-100">
                                <button type="submit" className="w-full flex items-center justify-center gap-2 text-white bg-brand-accent hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-3 text-center disabled:opacity-70" disabled={isSaving}>
                                    {isSaving ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <><FiSave /> Create Timer</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                {/* --- Right Column: Preview --- */}
                <div className="lg:col-span-2">
                    <div className="sticky top-24">
                        <h3 className="text-lg font-bold text-brand-heading flex items-center gap-2 mb-3"><FiEye /> Live Preview</h3>
                        <div className="bg-white p-1 rounded-lg border border-brand-border shadow-sm">
                            <TimerPreview
                                backgroundColor={backgroundColor}
                                digitColor={digitColor}
                                labelColor={labelColor}
                                endTime={time}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateTimer;
